import React, { useState, useEffect, useCallback } from 'react';
import { logsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ScrollText,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  Info,
  Monitor,
  Globe,
  User,
  Clock,
  FileText,
  RefreshCw,
} from 'lucide-react';

const Logs = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filtros
  const [method, setMethod] = useState('');
  const [usuario, setUsuario] = useState('');
  const [busca, setBusca] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  // Detail dialog
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      const params = { page: pageNum, limit: 50 };
      if (method) params.method = method;
      if (usuario) params.usuario = usuario;
      if (busca) params.busca = busca;
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;

      const res = await logsAPI.list(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      setPage(pageNum);
    } catch (error) {
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  }, [method, usuario, busca, dataInicio, dataFim]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await logsAPI.stats();
      setStats(res.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs(1);
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs(1);
  };

  const handleClearFilters = () => {
    setMethod('');
    setUsuario('');
    setBusca('');
    setDataInicio('');
    setDataFim('');
  };

  const methodBadge = (m) => {
    const styles = {
      POST: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      PUT: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      PATCH: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    };
    return styles[m] || 'bg-gray-100 text-gray-800';
  };

  const statusBadge = (code) => {
    if (code >= 200 && code < 300) return 'default';
    if (code >= 400 && code < 500) return 'outline';
    return 'destructive';
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR');
    } catch {
      return ts;
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleDateString('pt-BR');
    } catch {
      return ts;
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleTimeString('pt-BR');
    } catch {
      return ts;
    }
  };

  const parseDetalhes = (detalhes) => {
    if (!detalhes) return null;
    try {
      return JSON.parse(detalhes);
    } catch {
      return detalhes;
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <ScrollText className="w-7 h-7" />
            Logs do Sistema
          </h1>
          <p className="text-muted-foreground">Registro completo de todas as atividades</p>
        </div>
        <Button variant="outline" onClick={() => { fetchLogs(page); fetchStats(); }} data-testid="refresh-logs-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 max-w-sm">
          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total de Registros</p>
              <p className="text-2xl font-bold" data-testid="stats-total">{stats.total?.toLocaleString('pt-BR')}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Busca</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Endpoint, descrição..."
                  className="pl-8 h-9"
                  data-testid="filter-busca"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Método</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="h-9" data-testid="filter-method">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="POST">POST (Criação)</SelectItem>
                  <SelectItem value="PUT">PUT (Atualização)</SelectItem>
                  <SelectItem value="DELETE">DELETE (Exclusão)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Usuário</Label>
              <Input
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                placeholder="Nome ou e-mail"
                className="h-9"
                data-testid="filter-usuario"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data Início</Label>
              <Input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="h-9"
                data-testid="filter-data-inicio"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data Fim</Label>
              <Input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="h-9"
                data-testid="filter-data-fim"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button type="submit" size="sm" className="h-9 flex-1" data-testid="apply-filters-btn">
                <Search className="w-3.5 h-3.5 mr-1" />
                Filtrar
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-9" onClick={handleClearFilters} data-testid="clear-filters-btn">
                Limpar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Tabela de Logs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {total.toLocaleString('pt-BR')} registros encontrados
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              Página {page} de {totalPages}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <ScrollText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Nenhum log encontrado</p>
              <p className="text-sm">Os logs serão registrados conforme as ações forem realizadas no sistema</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[140px]">Data/Hora</TableHead>
                      <TableHead className="w-[80px]">Método</TableHead>
                      <TableHead>Ação</TableHead>
                      <TableHead>Usuário</TableHead>
                      <TableHead className="w-[120px]">IP</TableHead>
                      <TableHead className="w-[70px]">Status</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} className="hover:bg-muted/50">
                        <TableCell className="text-xs font-mono whitespace-nowrap">
                          <div>{formatDate(log.timestamp)}</div>
                          <div className="text-muted-foreground">{formatTime(log.timestamp)}</div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${methodBadge(log.method)}`}>
                            {log.method}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{log.descricao}</div>
                          <div className="text-xs text-muted-foreground font-mono truncate max-w-[300px]">{log.path}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{log.user_nome || '-'}</div>
                          <div className="text-xs text-muted-foreground">{log.user_role || ''}</div>
                        </TableCell>
                        <TableCell className="text-xs font-mono">{log.ip || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={statusBadge(log.status_code)} className="text-xs">
                            {log.status_code}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setSelectedLog(log)}
                            data-testid={`log-detail-${log.id}`}
                          >
                            <Info className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-2 p-4">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="p-3 border rounded-lg bg-background cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setSelectedLog(log)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${methodBadge(log.method)}`}>
                        {log.method}
                      </span>
                      <Badge variant={statusBadge(log.status_code)} className="text-xs">
                        {log.status_code}
                      </Badge>
                    </div>
                    <p className="font-medium text-sm">{log.descricao}</p>
                    <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                      <span>{log.user_nome || 'Sistema'}</span>
                      <span>{formatTimestamp(log.timestamp)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">IP: {log.ip || '-'}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => fetchLogs(page - 1)}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let p;
                  if (totalPages <= 7) {
                    p = i + 1;
                  } else if (page <= 4) {
                    p = i + 1;
                  } else if (page >= totalPages - 3) {
                    p = totalPages - 6 + i;
                  } else {
                    p = page - 3 + i;
                  }
                  return (
                    <Button
                      key={p}
                      variant={p === page ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => fetchLogs(p)}
                    >
                      {p}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => fetchLogs(page + 1)}
                data-testid="next-page-btn"
              >
                Próximo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Detalhes */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Detalhes do Log
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              {/* Data/Hora */}
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Clock className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Data e Hora</p>
                  <p className="font-mono text-sm">{formatTimestamp(selectedLog.timestamp)}</p>
                </div>
              </div>

              {/* Ação */}
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <FileText className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Ação</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${methodBadge(selectedLog.method)}`}>
                      {selectedLog.method}
                    </span>
                    <Badge variant={statusBadge(selectedLog.status_code)}>HTTP {selectedLog.status_code}</Badge>
                  </div>
                  <p className="font-medium mt-1">{selectedLog.descricao}</p>
                  <p className="text-xs font-mono text-muted-foreground mt-0.5 break-all">{selectedLog.path}</p>
                </div>
              </div>

              {/* Usuário */}
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <User className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Usuário</p>
                  <p className="font-medium">{selectedLog.user_nome || 'Não identificado'}</p>
                  <p className="text-xs text-muted-foreground">{selectedLog.user_email || '-'}</p>
                  <p className="text-xs text-muted-foreground">Cargo: {selectedLog.user_role || '-'}</p>
                  <p className="text-xs text-muted-foreground font-mono">ID: {selectedLog.user_id || '-'}</p>
                </div>
              </div>

              {/* Origem */}
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Globe className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Origem</p>
                  <p className="font-mono text-sm">IP: {selectedLog.ip || '-'}</p>
                </div>
              </div>

              {/* User Agent */}
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Monitor className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Navegador / Dispositivo</p>
                  <p className="text-xs font-mono break-all">{selectedLog.user_agent || '-'}</p>
                </div>
              </div>

              {/* Detalhes/Body */}
              {selectedLog.detalhes && selectedLog.detalhes !== '{}' && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Dados Enviados</p>
                  <pre className="text-xs font-mono bg-background p-3 rounded border overflow-auto max-h-48 whitespace-pre-wrap break-all">
                    {typeof parseDetalhes(selectedLog.detalhes) === 'object'
                      ? JSON.stringify(parseDetalhes(selectedLog.detalhes), null, 2)
                      : selectedLog.detalhes}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Logs;
